'use strict';

/**
 * EntityMark -- the single source of an entity's nomenclature.
 *
 * A drawing set works because every sheet carries a mark, the mark is stable, and any sheet can
 * reference any other by it. WI-003 is always WI-003. This is where that mark is formed and read
 * back, and it is deliberately the ONLY place either happens.
 *
 * It lives here, dependency-free, rather than inside the Pict provider, because the mark has to be
 * identical on both sides of the wire. The provider requires pict and so can never be loaded by the
 * server, but BlueprintRender draws the public /shared/:token page server-side and the Connections
 * endpoint projects marks into its payload. Had the formatter stayed client-only, those surfaces
 * would each have grown their own copy of the padding and prefix rules, and the whole point of a
 * designator is that it cannot drift.
 *
 * The prefix is not invented here either: it is declared on the descriptor beside NumberField
 * (EntityDescriptors.js), so a new entity brings its own mark and this module stays a formatter.
 */

const libDescriptors = require('./EntityDescriptors.js');

// Three digits is the width a plan sheet reads best at (WI-006, not WI-6). A floor, never a ceiling:
// past 999 the number simply grows, so WI-1024 is untouched.
const NumberPad = 3;

function descriptorFor(pEntityKey)
{
	return (libDescriptors.Entities || {})[pEntityKey] || null;
}

/** The prefix an entity marks its records with, or '' when it carries no number. */
function prefix(pEntityKey)
{
	let tmpDescriptor = descriptorFor(pEntityKey);
	return (tmpDescriptor && tmpDescriptor.Designator) ? tmpDescriptor.Designator : '';
}

/**
 * The mark: 'WI-301'. Takes either the record or the bare number, because callers have one or the
 * other depending on how much of the row they loaded. An entity with no number has no mark and says
 * so with an empty string rather than inventing one.
 */
function designator(pEntityKey, pRecordOrNumber)
{
	let tmpPrefix = prefix(pEntityKey);
	if (!tmpPrefix) { return ''; }
	let tmpNumber = 0;
	if (typeof pRecordOrNumber === 'number') { tmpNumber = pRecordOrNumber; }
	else if (pRecordOrNumber && typeof pRecordOrNumber === 'object')
	{
		let tmpDescriptor = descriptorFor(pEntityKey);
		tmpNumber = Number(pRecordOrNumber[tmpDescriptor.NumberField]) || 0;
	}
	else { tmpNumber = Number(pRecordOrNumber) || 0; }
	if (!tmpNumber) { return ''; }
	return tmpPrefix + '-' + String(tmpNumber).padStart(NumberPad, '0');
}

// Built lazily and cached: prefix -> entity key, for reading a mark back.
let _byPrefix = null;
function prefixIndex()
{
	if (_byPrefix) { return _byPrefix; }
	_byPrefix = {};
	let tmpEntities = libDescriptors.Entities || {};
	Object.keys(tmpEntities).forEach((pKey) =>
	{
		let tmpPrefix = tmpEntities[pKey].Designator;
		if (tmpPrefix) { _byPrefix[String(tmpPrefix).toUpperCase()] = pKey; }
	});
	return _byPrefix;
}

/**
 * Split something that looks like a mark into prefix and number: 'WI-019' and 'wi19' both give
 * { wi, 19 }; '019' and '19' both give { '', 19 }. Null when there is no number in it at all, which
 * is the common case and lets a caller fall straight through to a text search.
 *
 * Leading zeros are dropped on both sides, so the padding a mark is DISPLAYED with never has to be
 * typed. That is the point: a person reads WI-019 and types 19.
 */
function splitMark(pText)
{
	let tmpMatch = String(pText || '').trim().toLowerCase().match(/^([a-z]{1,4})?[\s-]*(\d+)$/);
	if (!tmpMatch) { return null; }
	return { Prefix: tmpMatch[1] || '', Number: parseInt(tmpMatch[2], 10) };
}

/**
 * Read a mark back into what it points at: 'WI-301' -> { Entity:'WorkItem', Number:301 }. Returns
 * null for anything that is not a mark of a known entity, so free text can be scanned without
 * guarding every match.
 */
function parse(pDesignator)
{
	let tmpSplit = splitMark(pDesignator);
	if (!tmpSplit || !tmpSplit.Prefix) { return null; }
	let tmpEntity = prefixIndex()[tmpSplit.Prefix.toUpperCase()];
	if (!tmpEntity) { return null; }
	return { Entity: tmpEntity, Number: tmpSplit.Number };
}

/**
 * Does this row's mark answer the query? Compared structurally against the row's OWN designator
 * rather than a prefix table, so a bare number matches that number on any entity and a query
 * carrying a prefix must match the entity too. A row with no designator falls back to its number.
 */
function matchesQuery(pRow, pQuery)
{
	let tmpWanted = splitMark(pQuery);
	if (!tmpWanted) { return false; }
	let tmpHas = splitMark(pRow && pRow.Designator);
	if (!tmpHas)
	{
		let tmpNumber = Number((pRow && (pRow.MarkNumber || pRow.Number)) || 0);
		return !tmpWanted.Prefix && tmpNumber > 0 && tmpNumber === tmpWanted.Number;
	}
	if (tmpHas.Number !== tmpWanted.Number) { return false; }
	return !tmpWanted.Prefix || tmpWanted.Prefix === tmpHas.Prefix;
}

module.exports = { NumberPad, prefix, designator, parse, splitMark, matchesQuery, descriptorFor };
